import { DatePipe } from '@angular/common';
import { Component, computed, effect, input, signal } from '@angular/core';
import { FormArray, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatExpansionModule } from '@angular/material/expansion';
import { IGamePlayDeliverableForm } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/deliverable-step/deliverable-list/game-play/game-play.interface';
import { CustomCtaBaseDirective, ECustomFieldType } from '@core/directives/custom-cta-base.directive';
import { CustomValuesService } from '@core/services/custom-values.service';
import { EPlatformFeature } from '@enums/campaign.enum';
import { TExpectedKpi } from '@interfaces/campaign-analytics.interface';
import { TranslateModule } from '@ngx-translate/core';
import { EExecutionType, IExecution } from '../../../execution-step/execution-list/execution/execution.interface';
import { ExecutionExpectedKpisService } from './execution-expected-kpis.service';
import { ExpectedKpiListComponent } from './expected-kpi-list/expected-kpi-list.component';
import { IExpectedKpiForm } from './expected-kpi-list/expected-kpi/expected-kpi.interface';

@Component({
  imports: [ReactiveFormsModule, TranslateModule, ExpectedKpiListComponent, DatePipe, MatExpansionModule],
  selector: 'app-execution-expected-kpis',
  templateUrl: './execution-expected-kpis.component.html',
})
export class ExecutionExpectedKpisComponent extends CustomCtaBaseDirective {
  public readonly platformFeature = input.required<EPlatformFeature>();
  public readonly availableKpis = input.required<TExpectedKpi[] | null>();
  public readonly gamePlayValues = input.required<FormArray<FormGroup<IGamePlayDeliverableForm>>['value']>();

  public readonly executionValue = computed((): IExecution => {
    this.executionValueVersion();

    return this.form().getRawValue();
  });
  public readonly expectedKpisForm = computed(
    () => this.form().get('expectedKpis') as FormArray<FormGroup<IExpectedKpiForm>>,
  );
  public readonly executionId = computed(() => this.form().get('id')?.value ?? '');
  public readonly executionTypeRaw = computed(() => this.executionValue().executionType as EExecutionType | null);
  public readonly targetsSetCount = computed(() => {
    this.expectedKpisValueVersion();
    const creatorId = this.creatorId();
    const isCustomMode = this.isCustomMode();
    const isViewMode = this.isViewMode();

    return this.expectedKpisForm().controls.filter((control) => {
      const expectedKpi = control.getRawValue();

      return (
        ExecutionExpectedKpisService.isExpectedKpiVisible(expectedKpi, creatorId, isCustomMode, isViewMode) &&
        ExecutionExpectedKpisService.isExpectedValueSet(expectedKpi.expectedValue)
      );
    }).length;
  });
  public readonly targetValuesActionTranslationKey = computed((): string => {
    if (this.isExpanded()) {
      return 'pages.campaignForm.sections.creatorsAndCtas.deliverableFlow.kpisStep.hideTargetValues';
    }

    if (this.targetsSetCount() === 0) {
      return 'pages.campaignForm.sections.creatorsAndCtas.deliverableFlow.kpisStep.addTargetValues';
    }

    return 'pages.campaignForm.sections.creatorsAndCtas.deliverableFlow.kpisStep.editTargetValues';
  });
  public readonly targetsSetLabelTranslationKey = computed((): string => {
    if (this.targetsSetCount() === 1) {
      return 'pages.campaignForm.sections.creatorsAndCtas.deliverableFlow.kpisStep.targetSet.single';
    }

    return 'pages.campaignForm.sections.creatorsAndCtas.deliverableFlow.kpisStep.targetSet.multiple';
  });

  public readonly EExecutionType = EExecutionType;

  protected readonly customValueConfig = {
    nestedArray: {
      getFormArray: (): FormArray<FormGroup<IExpectedKpiForm>> => this.expectedKpisForm(),
      managedFields: [{ isMonitoringDisabled: true, name: 'expectedValue', type: ECustomFieldType.number }],
    },
  };

  private readonly executionValueVersion = signal<number>(0);
  private readonly expectedKpisValueVersion = signal<number>(0);

  constructor(customValuesService: CustomValuesService) {
    super(customValuesService);

    effect((onCleanup) => {
      const form = this.form();
      const formSubscription = form.valueChanges.subscribe(() => {
        this.executionValueVersion.update((version) => version + 1);
      });
      const expectedKpisSubscription = this.expectedKpisForm().valueChanges.subscribe(() => {
        this.expectedKpisValueVersion.update((version) => version + 1);
      });

      onCleanup(() => {
        formSubscription.unsubscribe();
        expectedKpisSubscription.unsubscribe();
      });
    });
  }

  public override afterModeChange(): void {
    setTimeout(() => this.expectedKpisValueVersion.update((version) => version + 1));
  }
}
